import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface SiblingSettings {
  presenceEnabled: boolean;
  nudgesEnabled: boolean;
  sharedAdventuresEnabled: boolean;
  collectionViewingEnabled: boolean;
  weeklyComparisonEnabled: boolean;
  nudgeWeeklyCap: number;
}

interface SiblingAdmin {
  settings: SiblingSettings | null;
  nudgesThisWeek: number;
  note: string;
}

const TOGGLES: { key: keyof SiblingSettings; label: string; detail: string }[] = [
  {
    key: 'presenceEnabled',
    label: 'Show when the other one is playing',
    detail: 'A gentle "Aubrey is exploring now" on the home screen. No chat, no messaging.',
  },
  {
    key: 'weeklyComparisonEnabled',
    label: 'Show missions done this week, side by side',
    detail:
      'Each child against their OWN weekly target: "You: 3 of 4 · Aubrey: 4 of 4". Never levels, skills, scores or reading age — the app has no way to show those to a child, by design.',
  },
  {
    key: 'nudgesEnabled',
    label: 'Let one invite the other',
    detail:
      'A positive line at sign-in, capped per week, always leaving the choice with the child. Never shown during an activity.',
  },
  {
    key: 'sharedAdventuresEnabled',
    label: 'Shared adventures',
    detail:
      'One banner over two entirely different missions on the same day, with a shared celebration at the end.',
  },
  {
    key: 'collectionViewingEnabled',
    label: 'Let them see each other’s shelf',
    detail: 'Collection counts, completed sets and companion. Nothing academic.',
  },
];

/**
 * The sibling controls.
 *
 * Head-to-head competition is not on this page because it does not exist in the
 * app at all - it was never built, so there is nothing to switch off.
 */
export function AdminSiblings() {
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['admin', 'siblings'],
    queryFn: () => api<SiblingAdmin>('/admin/siblings'),
  });

  const update = useMutation({
    mutationFn: (patch: Partial<SiblingSettings>) =>
      api('/admin/siblings', { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'siblings'] }),
  });

  const settings = data.data?.settings;

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>Between the two of them</h1>
          <p className="muted">
            Everything here is off-able, and everything is participation only.
          </p>
        </div>
      </div>

      <section className="card card--warning">
        <h2>What is never compared</h2>
        <p className="muted">
          Curriculum level, skills mastered, scores, reading level and correct answers are never
          shown to one child about the other. That is enforced in code by an allowlist, not by a
          setting on this page — there is no toggle that could turn it on.
        </p>
        <p className="muted">
          There is also no head-to-head mode. It was never built, so there is nothing here to switch
          off.
        </p>
      </section>

      {data.isPending && <p className="muted">Loading…</p>}

      {settings && (
        <section className="card">
          {TOGGLES.map((toggle) => (
            <label key={toggle.key} className="switch-row" style={{ marginBottom: 18 }}>
              <input
                type="checkbox"
                checked={Boolean(settings[toggle.key])}
                onChange={(e) => update.mutate({ [toggle.key]: e.target.checked })}
              />
              <span>
                {toggle.label}
                <br />
                <small className="muted">{toggle.detail}</small>
              </span>
            </label>
          ))}

          <label>
            Invitations per week, each
            <input
              type="number"
              min={0}
              max={7}
              defaultValue={settings.nudgeWeeklyCap}
              onBlur={(e) => update.mutate({ nudgeWeeklyCap: Number(e.target.value) })}
            />
          </label>
          <p className="muted">
            Set it to zero to stop them entirely without switching the feature off.
          </p>
        </section>
      )}

      <section className="card">
        <h2>Usage</h2>
        <p className="muted">Invitations shown this week: {data.data?.nudgesThisWeek ?? 0}</p>
        <p className="muted">{data.data?.note}</p>
      </section>
    </div>
  );
}
